import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { EducationType, PaymentCurrency } from '@hygge/shared';
import { api } from '@/lib/api';
import { employeesKeys } from '@/hooks/use-employees';

/**
 * The employee card's FOP payment details and Education blocks [HW-1008].
 * Payment details are served apart from the profile (`GET /employees/:id`
 * never carries them), behind their own read rule — see
 * `canViewPaymentDetails`.
 */

export interface PaymentDetails {
  fopName: string;
  iban: string | null;
  taxId: string | null;
  swift: string | null;
  currency: PaymentCurrency | null;
  registrationAddress: string | null;
  kved: string[];
  updatedAt: string;
}

export type PaymentDetailsInput = Omit<PaymentDetails, 'updatedAt'>;

export interface Education {
  id: string;
  type: EducationType;
  institution: string;
  specialty: string | null;
  /** YYYY-MM-DD */
  startDate: string | null;
  /** YYYY-MM-DD; null = still studying */
  endDate: string | null;
}

export type EducationInput = Omit<Education, 'id'>;

export const employeeFinanceKeys = {
  paymentDetails: (employeeId: string) => ['employee', employeeId, 'payment-details'] as const,
  education: (employeeId: string) => ['employee', employeeId, 'education'] as const,
};

/**
 * The change history records both blocks, so a save makes the History tab's
 * pages and the Job tab's preview stale (keys from use-employee-audit-log.ts).
 */
function invalidateHistory(queryClient: QueryClient, employeeId: string) {
  for (const key of ['audit-log', 'audit-log-preview']) {
    queryClient.invalidateQueries({ queryKey: [...employeesKeys.detail(employeeId), key] });
  }
}

export function usePaymentDetails(employeeId: string, opts: { enabled: boolean }) {
  return useQuery({
    queryKey: employeeFinanceKeys.paymentDetails(employeeId),
    queryFn: async () =>
      (
        await api.get<{ success: boolean; data: PaymentDetails | null }>(
          `/employees/${employeeId}/payment-details`
        )
      ).data.data,
    enabled: opts.enabled && !!employeeId,
  });
}

export function useSavePaymentDetails(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PaymentDetailsInput) =>
      (
        await api.put<{ success: boolean; data: PaymentDetails }>(
          `/employees/${employeeId}/payment-details`,
          input
        )
      ).data.data,
    meta: { suppressGlobalToast: true },
    onSuccess: (saved) => {
      queryClient.setQueryData(employeeFinanceKeys.paymentDetails(employeeId), saved);
      invalidateHistory(queryClient, employeeId);
    },
  });
}

export function useClearPaymentDetails(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/employees/${employeeId}/payment-details`);
    },
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.setQueryData(employeeFinanceKeys.paymentDetails(employeeId), null);
      invalidateHistory(queryClient, employeeId);
    },
  });
}

export function useEducation(employeeId: string) {
  return useQuery({
    queryKey: employeeFinanceKeys.education(employeeId),
    queryFn: async () =>
      (await api.get<{ success: boolean; data: Education[] }>(`/employees/${employeeId}/education`))
        .data.data,
    enabled: !!employeeId,
  });
}

function useEducationMutation<T>(employeeId: string, fn: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta: { suppressGlobalToast: true },
    // The server owns the order (newest first), so refetch rather than splice.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeFinanceKeys.education(employeeId) });
      invalidateHistory(queryClient, employeeId);
    },
  });
}

export const useCreateEducation = (employeeId: string) =>
  useEducationMutation(employeeId, (input: EducationInput) =>
    api.post(`/employees/${employeeId}/education`, input)
  );

export const useUpdateEducation = (employeeId: string) =>
  useEducationMutation(employeeId, ({ id, ...input }: Education) =>
    api.put(`/employees/${employeeId}/education/${id}`, input)
  );

export const useDeleteEducation = (employeeId: string) =>
  useEducationMutation(employeeId, (id: string) =>
    api.delete(`/employees/${employeeId}/education/${id}`)
  );
